/**
 * JitConfigForm - JIT Provisioning Configuration
 * Allows configuring claim mappings and default values for JIT user creation
 */

import React, { useState } from 'react';

const COMMON_CLAIMS = ['nickname', 'email', 'phone_number', 'name', 'sub', 'preferred_username'];
const USER_TYPES = ['client_user', 'client_admin'];

const JitConfigForm = ({ config, onChange }) => {
  const [newMappingField, setNewMappingField] = useState('');
  const [newMappingClaim, setNewMappingClaim] = useState('');
  const [newDefaultField, setNewDefaultField] = useState('');
  const [newDefaultValue, setNewDefaultValue] = useState('');

  const {
    jit_provisioning_enabled = false,
    user_type_for_jit = 'client_user',
    claim_mappings = {},
    default_field_values = {},
    default_role = 'user',
  } = config;

  const handleChange = (field, value) => {
    onChange({ ...config, [field]: value });
  };

  const handleMappingChange = (field, newClaim) => {
    const updated = { ...claim_mappings, [field]: newClaim };
    handleChange('claim_mappings', updated);
  };

  const handleRemoveMapping = (field) => {
    const updated = { ...claim_mappings };
    delete updated[field];
    handleChange('claim_mappings', updated);
  };

  const handleAddMapping = () => {
    if (!newMappingField.trim() || !newMappingClaim.trim()) return;
    const updated = { ...claim_mappings, [newMappingField.trim()]: newMappingClaim.trim() };
    handleChange('claim_mappings', updated);
    setNewMappingField('');
    setNewMappingClaim('');
  };

  const handleDefaultChange = (field, newValue) => {
    const updated = { ...default_field_values, [field]: newValue };
    handleChange('default_field_values', updated);
  };

  const handleRemoveDefault = (field) => {
    const updated = { ...default_field_values };
    delete updated[field];
    handleChange('default_field_values', updated);
  };

  const handleAddDefault = () => {
    if (!newDefaultField.trim()) return;
    const updated = { ...default_field_values, [newDefaultField.trim()]: newDefaultValue };
    handleChange('default_field_values', updated);
    setNewDefaultField('');
    setNewDefaultValue('');
  };

  return (
    <div className="jit-config-form">
      <div className="mB-15">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="jitEnabled"
            checked={jit_provisioning_enabled}
            onChange={(e) => handleChange('jit_provisioning_enabled', e.target.checked)}
          />
          <label className="form-check-label fw-bold" htmlFor="jitEnabled">
            Enable JIT (Just-In-Time) Provisioning
          </label>
          <p className="text-muted small mB-0">
            Automatically create users on first login via this IDP
          </p>
        </div>
      </div>

      {jit_provisioning_enabled && (
        <>
          <div className="row mB-15">
            <div className="col-md-6">
              <label className="form-label">User Type for JIT Users</label>
              <select
                className="form-select"
                value={user_type_for_jit}
                onChange={(e) => handleChange('user_type_for_jit', e.target.value)}
              >
                {USER_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Default Role</label>
              <select
                className="form-select"
                value={default_role}
                onChange={(e) => handleChange('default_role', e.target.value)}
              >
                <option value="user">user</option>
                <option value="admin">admin</option>
                <option value="viewer">viewer</option>
              </select>
            </div>
          </div>

          <hr />

          {/* Claim Mappings Section */}
          <div className="mB-20">
            <h6>Claim Mappings</h6>
            <p className="text-muted small">
              Map user fields to IDP token claim paths. Use dot notation for nested claims (e.g., app_metadata.username).
            </p>

            {Object.entries(claim_mappings).length > 0 ? (
              <div className="table-responsive mB-10">
                <table className="table table-sm table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>User Field</th>
                      <th>IDP Claim</th>
                      <th style={{ width: '60px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(claim_mappings).map(([field, claim]) => (
                      <tr key={field}>
                        <td>
                          <code>{field}</code>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={claim}
                            onChange={(e) => handleMappingChange(field, e.target.value)}
                            list="common-claims"
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveMapping(field)}
                          >
                            <i className="ti-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-warning small mB-10">
                No claim mappings configured. Add mappings below.
              </div>
            )}

            <datalist id="common-claims">
              {COMMON_CLAIMS.map((claim) => (
                <option key={claim} value={claim} />
              ))}
            </datalist>

            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <label className="form-label small">User Field</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={newMappingField}
                  onChange={(e) => setNewMappingField(e.target.value)}
                  placeholder="e.g., user_name"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small">IDP Claim</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={newMappingClaim}
                  onChange={(e) => setNewMappingClaim(e.target.value)}
                  placeholder="e.g., nickname"
                  list="common-claims"
                />
              </div>
              <div className="col-md-4">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleAddMapping}
                  disabled={!newMappingField.trim() || !newMappingClaim.trim()}
                >
                  <i className="ti-plus mR-5"></i>
                  Add Mapping
                </button>
              </div>
            </div>
          </div>

          <hr />

          {/* Default Field Values Section */}
          <div className="mB-15">
            <h6>Default Field Values</h6>
            <p className="text-muted small">
              Fallback values when IDP claims are missing. Phone number is commonly missing from IDP tokens.
            </p>

            {Object.entries(default_field_values).length > 0 ? (
              <div className="table-responsive mB-10">
                <table className="table table-sm table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>Field</th>
                      <th>Default Value</th>
                      <th style={{ width: '60px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(default_field_values).map(([field, value]) => (
                      <tr key={field}>
                        <td>
                          <code>{field}</code>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={String(value)}
                            onChange={(e) => handleDefaultChange(field, e.target.value)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveDefault(field)}
                          >
                            <i className="ti-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted small mB-10">No default values configured.</p>
            )}

            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <label className="form-label small">Field</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={newDefaultField}
                  onChange={(e) => setNewDefaultField(e.target.value)}
                  placeholder="e.g., phone_numebr"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small">Default Value</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={newDefaultValue}
                  onChange={(e) => setNewDefaultValue(e.target.value)}
                  placeholder="e.g., 0000000000"
                />
              </div>
              <div className="col-md-4">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleAddDefault}
                  disabled={!newDefaultField.trim()}
                >
                  <i className="ti-plus mR-5"></i>
                  Add Default
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default JitConfigForm;
